import React, { createContext, useContext, useState, useCallback } from "react";
import { X, Mail, Send } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { confirmationTarget } from "@/lib/contactConfirmation";
import { trackLead } from "@/lib/googleTags";

const ContactModalContext = createContext(null);
export const useContactModal = () => useContext(ContactModalContext);

const EMPTY = { name: "", email: "", phone: "", message: "" };

export function ContactModalProvider({ children }) {
  const [state, setState] = useState({ open: false, items: [], source: "contact" });
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const open = useCallback((opts = {}) => {
    setState({ open: true, items: opts.items || [], source: opts.source || "contact" });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const navigate = useNavigate();
  // Confirmation d'envoi, partagée par TOUS les formulaires de contact du
  // site (section Contact en style simple ou en étapes, fenêtre de contact,
  // rappel du bandeau…) : elle vit ici parce que ce provider est déjà monté
  // à la racine (App.js) — chaque formulaire appelle notifySent() plutôt que
  // de porter sa propre confirmation. Elle mène à la page
  // /contact/confirmation, y compris dans le Canvas du Studio de thème
  // (les paramètres d'aperçu sont conservés, voir lib/contactConfirmation.js).
  //
  // `message` (optionnel) : un formulaire dont la boutique a personnalisé le
  // texte de confirmation le passe ici, sinon la phrase générique s'affiche.
  // `details.name` (optionnel) personnalise le titre de la confirmation.
  //
  // À n'appeler QU'APRÈS une réponse OK de /contact-request : c'est ce qui
  // pose le jeton d'accès de la page de confirmation (lib/contactConfirmation.js).
  const notifySent = useCallback((message = "", details = {}) => {
    // Tous les formulaires de contact/devis passent ici après un envoi OK :
    // c'est LE point où compter une demande (generate_lead Google).
    trackLead(details.source || "contact");
    const target = confirmationTarget({ message, name: details.name });
    navigate(target.to, { state: target.state });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact-request", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        items: (state.items || []).map((i) => ({
          name: i.name,
          quantity: i.quantity || 1,
          price: typeof i.price === "number" ? i.price : null,
          slug: i.slug || null,
        })),
        source: state.source,
      });
      const sentName = form.name;
      setForm(EMPTY);
      // La fenêtre du formulaire se ferme AVANT la confirmation : deux
      // fenêtres empilées seraient illisibles, et le voile de la seconde
      // assombrirait la première.
      close();
      notifySent("", { name: sentName, source: state.source });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <ContactModalContext.Provider value={{ open, close, notifySent }}>
      {children}
      {state.open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="contact-modal">
          <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={close} />
          <div className="relative w-full max-w-lg bg-cream rounded-2xl shadow-2xl border-control border-stone max-h-[92vh] overflow-y-auto animate-fade-up">
            <div className="flex items-start justify-between p-6 pb-4 border-b border-stone">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#635BFF,#38BDF8)" }}>
                  <Mail className="h-5 w-5 text-white" />
                </span>
                <div>
                  <h2 className="font-display font-semibold text-xl text-ink leading-tight">Parlons de votre projet</h2>
                  <p className="text-[13px] text-graphite">Réponse sous 24h avec une estimation.</p>
                </div>
              </div>
              <button data-testid="contact-modal-close" onClick={close} aria-label="Fermer" className="text-ash hover:text-ink transition-colors p-1 -mr-1 -mt-1">
                <X className="h-5 w-5" />
              </button>
            </div>

            {state.items && state.items.length > 0 && (
              <div className="px-6 pt-4">
                <p className="text-[11px] uppercase tracking-[0.15em] text-ash mb-2">Formule(s) sélectionnée(s)</p>
                <ul className="rounded-xl border-control border-stone bg-white divide-y divide-stone" data-testid="contact-modal-items">
                  {state.items.map((i, idx) => (
                    <li key={idx} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-ink">{i.quantity > 1 ? `${i.quantity}× ` : ""}{i.name}</span>
                      {typeof i.price === "number" && i.price > 0 && (
                        <span className="font-mono text-graphite">{eur(i.price)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={submit} className="p-6 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={set("name")} placeholder="Nom complet" data-testid="contact-modal-name" className="w-full rounded-xl border-control border-stone bg-white px-4 py-3 text-[14px] text-ink outline-none focus:border-gold transition-colors" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="contact-modal-email" className="w-full rounded-xl border-control border-stone bg-white px-4 py-3 text-[14px] text-ink outline-none focus:border-gold transition-colors" />
              </div>
              <input value={form.phone} onChange={set("phone")} placeholder="Téléphone (optionnel)" data-testid="contact-modal-phone" className="w-full rounded-xl border-control border-stone bg-white px-4 py-3 text-[14px] text-ink outline-none focus:border-gold transition-colors" />
              <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Décrivez votre besoin…" data-testid="contact-modal-message" className="w-full rounded-xl border-control border-stone bg-white px-4 py-3 text-[14px] text-ink outline-none focus:border-gold transition-colors resize-none" />
              <button type="submit" disabled={sending} data-testid="contact-modal-submit" className="w-full flex items-center justify-center gap-2 bg-ink text-white py-3.5 rounded-xl text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                <Send className="h-4 w-4" /> {sending ? "Envoi…" : "Envoyer ma demande"}
              </button>
            </form>
          </div>
        </div>
      )}
    </ContactModalContext.Provider>
  );
}
